import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useBasePath } from '../../../../lib/config/nav-helpers';
import { DataCard, StatusBadge, SkeletonList, QueryError, NotConnected } from '../../../../components/ui';
import { teamSnapshotRows, type TeamForecastResponse } from '../../../../lib/team-forecast';

export interface TeamSnapshotWidgetProps {
  teamSnapshot: { data?: TeamForecastResponse; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
}

const k = (cents: number) => `$${Math.round(cents / 100_000)}k`;

export function TeamSnapshotWidget({ teamSnapshot }: TeamSnapshotWidgetProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const ts = teamSnapshot.data;
  const reps = ts ? teamSnapshotRows(ts) : [];
  // The coverage rule: no CRM connected and synced means the API sent no pipeline numbers (null), not $0k.
  const unseen = !!ts && (ts.reps === null || ts.totals === null);

  return (
    <DataCard
      title="Team Snapshot"
      subtitle="Open pipeline and closed this month, by rep"
      badge={ts?.reps ? <StatusBadge status="info" label={`${ts.reps.length} reps`} size="xs" /> : undefined}
    >
      {teamSnapshot.isLoading ? <SkeletonList rows={3} /> : teamSnapshot.isError ? <QueryError error={teamSnapshot.error} onRetry={() => teamSnapshot.refetch()} compact /> : unseen ? (
        <NotConnected need="crm" />
      ) : !ts?.totals || reps.length === 0 ? (
        <p className="text-2xs text-ink-dim italic">No team members on record yet.</p>
      ) : (
        <div className="space-y-3">
          {/* Team totals strip — no commit/best case: those are HubSpot-probability sums. */}
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-surface-1 px-3 py-2">
            <div className="text-center">
              <p className="text-2xs text-ink-dim">Open pipeline</p>
              <p className="text-xs font-semibold font-mono tabular-nums text-ink">{k(ts.totals.pipelineCents)}</p>
            </div>
            <div className="text-center">
              <p className="text-2xs text-ink-dim">Closed this month</p>
              <p className="text-xs font-semibold font-mono tabular-nums text-primary">{k(ts.totals.closedCents)}</p>
            </div>
          </div>

          {/* Rep breakdown */}
          <div className="space-y-1.5">
            {reps.map((r, i) => (
              <button
                type="button"
                key={r.key}
                onClick={() => navigate(basePath === '/preview' ? '/preview/sales/leaderboard' : '/sales/leaderboard')}
                className="w-full text-left flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-surface-1 transition-colors cursor-pointer"
              >
                <span className="w-3 text-2xs text-ink-dim font-mono">{i + 1}</span>
                <span className="flex-1 text-2xs font-medium text-ink truncate">{r.label}</span>
                <span className="text-2xs font-mono tabular-nums text-ink-muted" title="Open pipeline">{k(r.pipelineCents)}</span>
                <span className="w-10 text-right text-2xs font-mono tabular-nums text-primary" title="Closed this month">{k(r.closedCents)}</span>
                {r.quotaAttainment !== null && (
                  <>
                    <div className="w-12">
                      <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
                        <div
                          className={clsx('h-full rounded-full', r.quotaAttainment >= 100 ? 'bg-success' : r.quotaAttainment >= 70 ? 'bg-primary' : 'bg-warn')}
                          style={{ width: `${Math.min(r.quotaAttainment, 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className="w-8 text-right text-2xs font-mono tabular-nums text-ink-dim">{Math.round(r.quotaAttainment)}%</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </DataCard>
  );
}
